import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  ButtonIcon,
  ButtonSelect,
  Chip,
  Icon,
  Illustration,
  type IllustrationKind,
  Loader,
  Menu,
  MenuMulti,
  type MenuMultiOption,
  type MenuOption,
  TableSortable,
  Typography,
} from '@saviynt/design-system';

import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../../shared/hooks';
import {
  type IdentitySearchRow,
  useGetInventoryFiltersQuery,
  useLazySearchIdentitiesQuery,
  useSearchIdentitiesQuery,
} from '../../../../data-layer/licenseManagementApi';

import {
  API_FILTER_CATEGORIES,
  API_FILTER_LABELS,
  buildColumns,
  type ColumnDef,
  COLUMNS_BY_CATEGORY,
  DEFAULT_ENABLED_FILTER_IDS,
  EMPTY_SELECTIONS,
  type FueFilterDef,
  type FUEInventoryRow,
  IDENTITY_CATEGORIES,
  IDENTITY_FILTERS_BY_CATEGORY,
  LOCKED_FILTERS_BY_CATEGORY,
  ROW_MAPPED_API_FIELDS,
  SORT_OPTIONS,
} from './constants/fueInventoryTableConstants';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';

import './FUEInventoryTable.css';

interface FUEInventoryTableProps {
  vendor: string;
  onRowClick?: (user: FUEInventoryRow) => void;
  isFilterBarOpen?: boolean;
  filterSelections?: Record<string, string[]>;
  onFilterChange?: (filterId: string, values: string[]) => void;
  onClearFilters?: () => void;
  enabledFilterIds?: string[];
  onEnabledFilterIdsChange?: (ids: string[]) => void;
  isError?: boolean;
  onRetry?: () => void;
  searchText?: string;
  /** When true the table shrinks its width to leave room for the side panel */
  isPanelOpen?: boolean;
}

// Converts a plural API key to a singular filter ID.
// e.g. "statuses" → "status", "tiers" → "tier", "departments" → "department"
function toFilterId(apiKey: string): string {
  if (apiKey.endsWith('ses')) {
    return apiKey.slice(0, -2);
  } // statuses → status

  if (apiKey.endsWith('ies')) {
    return `${apiKey.slice(0, -3)}y`;
  } // categories → category

  if (apiKey.endsWith('s')) {
    return apiKey.slice(0, -1);
  } // tiers → tier

  return apiKey;
}

// Converts a camelCase id to a Title Case label.
// e.g. "recommendedTier" → "Recommended Tier", "status" → "Status"
function toAutoLabel(id: string): string {
  return id.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

/**
 * Formats a tier name for display by removing a trailing "User" word.
 * e.g. "Advanced User" → "Advanced", "Self-Service User" → "Self-Service",
 * "Core User" → "Core", "Developer" → "Developer".
 */
function formatTierLabel(tier: string): string {
  if (!tier) {
    return tier;
  }

  return tier.replace(/\s+users?$/i, '').trim();
}

/** Maps a tier name to the CSS class that applies its exact hex color. */
function getTierChipClass(tier: string): string {
  if (!tier) {
    return 'LicenseManagement-tierChip--unknown';
  }

  if (tier.startsWith('Advanced')) {
    return 'LicenseManagement-tierChip--advanced';
  }

  if (tier.startsWith('Core')) {
    return 'LicenseManagement-tierChip--core';
  }

  if (tier.startsWith('Self-Service')) {
    return 'LicenseManagement-tierChip--selfService';
  }

  if (tier.startsWith('Developer')) {
    return 'LicenseManagement-tierChip--developer';
  }

  return 'LicenseManagement-tierChip--unknown';
}

function getStatusChipColor(
  status: string
): 'success' | 'critical' | 'warning' {
  if (status === 'Over-licensed') {
    return 'critical';
  }

  if (status === 'Dormant') {
    return 'warning';
  }

  return 'success';
}

/**
 * @description Renders the FUE (Full User Equivalent) inventory table for a
 * license vendor. Displays identities grouped by identity-type tabs (Human,
 * non-Human, etc.), with dynamic API-driven columns and filters, client-side
 * filtering of the fetched result set, infinite-scroll pagination, sortable
 * columns, and loading/error/empty states. Selecting a row invokes `onRowClick`
 * with the mapped row data.
 *
 * @param props - Component props.
 * @param props.vendor {string} - Vendor key used to scope the inventory query.
 * @param props.onRowClick {(user: FUEInventoryRow) => void} [optional] - Called with the mapped row when a table row is clicked.
 * @param props.isFilterBarOpen {boolean} [optional] - Whether the filter bar is displayed. Defaults to false.
 * @param props.filterSelections {Record<string, string[]>} [optional] - Currently selected filter values keyed by filter id. Defaults to an empty selection.
 * @param props.onFilterChange {(filterId: string, values: string[]) => void} [optional] - Called when a filter's selected values change.
 * @param props.onClearFilters {() => void} [optional] - Called when all filters are cleared.
 * @param props.enabledFilterIds {string[]} [optional] - Controlled list of enabled filter ids; falls back to internal state when omitted.
 * @param props.onEnabledFilterIdsChange {(ids: string[]) => void} [optional] - Called when the set of enabled filter ids changes.
 * @param props.isError {boolean} [optional] - Whether an external error state should be shown. Defaults to false.
 * @param props.onRetry {() => void} [optional] - Called when the user retries after an error.
 * @param props.searchText {string} [optional] - Free-text search applied to the identity query.
 * @param props.isPanelOpen {boolean} [optional] - When true, the table shrinks its width to leave room for the side panel. Defaults to false.
 * @returns {React.ReactElement} The rendered FUE inventory table.
 */
function FUEInventoryTable({
  vendor,
  onRowClick = undefined,
  isFilterBarOpen = false,
  filterSelections = EMPTY_SELECTIONS,
  onFilterChange = undefined,
  onClearFilters = undefined,
  enabledFilterIds: enabledFilterIdsProp,
  onEnabledFilterIdsChange,
  isError = false,
  onRetry = undefined,
  searchText = undefined,
  isPanelOpen = false,
}: FUEInventoryTableProps): React.ReactElement {
  const localize = useInvertedLocalize();
  const cloudLicenseKey = useTypedSelector(
    (state) => state.aag.licenseManagement.cloudLicenseKey
  );

  const [selectedCategory, setSelectedCategory] = useState<string>('Human');
  const [columns, setColumns] = useState<ColumnDef[]>(
    COLUMNS_BY_CATEGORY.Human
  );
  const [localEnabledFilterIds, setLocalEnabledFilterIds] = useState<string[]>(
    DEFAULT_ENABLED_FILTER_IDS
  );
  const [isAddRemoveMenuOpen, setIsAddRemoveMenuOpen] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | undefined>(
    undefined
  );

  const { data: filtersData } = useGetInventoryFiltersQuery({
    vendor,
    cloudLicenseKey,
  });

  // Identity type tabs — driven by the filters API; fall back to static list
  const identityTypeList = useMemo<string[]>(
    () =>
      filtersData?.filters.identityTypes.map((t) => t.value) ??
      Array.from(IDENTITY_CATEGORIES),
    [filtersData]
  );

  // Snap to first available category if current one is not in the API response
  useEffect(() => {
    if (
      identityTypeList.length > 0 &&
      !identityTypeList.includes(selectedCategory)
    ) {
      setSelectedCategory(identityTypeList[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityTypeList]);

  const enabledFilterIds = enabledFilterIdsProp ?? localEnabledFilterIds;

  const setEnabledFilterIds = (updater: (prev: string[]) => string[]) => {
    const next = updater(enabledFilterIds);

    if (onEnabledFilterIdsChange) {
      onEnabledFilterIdsChange(next);
    } else {
      setLocalEnabledFilterIds(next);
    }
  };

  const isFirstCategoryRender = useRef(true);

  // Build the full filter list dynamically:
  //
  // 1. API-driven filters — one entry per key returned by filtersData.filters
  //    (excluding "identityTypes" which drives the category tabs).
  //    Any new key the API adds in the future is automatically included.
  //
  // 2. Identity-specific filters — userId (Human) / jobId (non-Human).
  //    Their options come from dynamicFilterOptions built from the API result set below.
  //    Only the entries for the currently selected category are appended.
  const resolvedFilters = useMemo<FueFilterDef[]>(() => {
    const apiFiltersObj = filtersData?.filters;

    const apiDriven: FueFilterDef[] = apiFiltersObj
      ? Object.entries(apiFiltersObj)
          .filter(([key]) => key !== 'identityTypes')
          .map(([key, options]) => {
            const id = toFilterId(key);

            return {
              id,
              apiKey: key,
              label: API_FILTER_LABELS[key] ?? toAutoLabel(id),
              category: API_FILTER_CATEGORIES[key] ?? 'Other',
              options: options as FueFilterDef['options'],
            };
          })
      : [];

    const identityFilters =
      IDENTITY_FILTERS_BY_CATEGORY[selectedCategory] ?? [];

    return [...apiDriven, ...identityFilters];
  }, [filtersData, selectedCategory]);

  useEffect(() => {
    if (isFirstCategoryRender.current) {
      isFirstCategoryRender.current = false;

      return;
    }

    onClearFilters?.();
    setSelectedRowIndex(undefined);
    const lockedIds =
      LOCKED_FILTERS_BY_CATEGORY[selectedCategory] ??
      DEFAULT_ENABLED_FILTER_IDS;

    if (onEnabledFilterIdsChange) {
      onEnabledFilterIdsChange([...lockedIds]);
    } else {
      setLocalEnabledFilterIds([...lockedIds]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  useEffect(() => {
    if (!isPanelOpen) {
      setSelectedRowIndex(undefined);
    }
  }, [isPanelOpen]);

  // Convert filterSelections to the API's expected shape — arrays per key.
  // Filter IDs are singular (e.g. "status") but the search API expects the original plural keys
  // (e.g. "statuses") — use the apiKey stored on each filter definition for that mapping.
  const apiFilters = useMemo<Record<string, string[]>>(() => {
    const idToApiKey = Object.fromEntries(
      resolvedFilters.filter((f) => f.apiKey).map((f) => [f.id, f.apiKey!])
    );
    const result: Record<string, string[]> = {};

    Object.entries(filterSelections).forEach(([key, values]) => {
      if (values.length > 0) {
        result[idToApiKey[key] ?? key] = values;
      }
    });

    return result;
  }, [filterSelections, resolvedFilters]);

  const {
    data: identitySearchData,
    isFetching: isIdentitiesFetching,
    isLoading: isIdentitiesLoading,
    isError: isSearchError,
    refetch: refetchSearch,
  } = useSearchIdentitiesQuery({
    vendor,
    identityType: selectedCategory,
    filters: apiFilters,
    cloudLicenseKey,
    searchText: searchText ?? null,
  });

  // ── Infinite scroll ─────────────────────────────────────────────────────────
  const [allIdentities, setAllIdentities] = useState<IdentitySearchRow[]>([]);
  const [nextSearchAfter, setNextSearchAfter] = useState<string[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [fetchMore, { isFetching: isFetchingMore }] =
    useLazySearchIdentitiesQuery();

  // Reset accumulated rows when the first page arrives or category changes.
  useEffect(() => {
    const results = identitySearchData?.results ?? [];

    setAllIdentities(results);
    const cursor = identitySearchData?.searchAfter ?? null;

    setNextSearchAfter(cursor);
    setHasMore(cursor !== null);
  }, [selectedCategory, identitySearchData]);

  const loadNextPage = useCallback(() => {
    if (!nextSearchAfter || isFetchingMore) {
      return;
    }

    fetchMore({
      vendor,
      identityType: selectedCategory,
      filters: apiFilters,
      searchAfter: nextSearchAfter,
      cloudLicenseKey,
      searchText: searchText ?? null,
    })
      .then((result) => {
        if (result.data) {
          const newRows = result.data.results ?? [];

          setAllIdentities((prev) => [...prev, ...newRows]);
          const cursor = result.data.searchAfter ?? null;

          setNextSearchAfter(cursor);
          setHasMore(cursor !== null);
        }
      })
      .catch(() => {});
  }, [
    nextSearchAfter,
    isFetchingMore,
    fetchMore,
    vendor,
    selectedCategory,
    apiFilters,
    cloudLicenseKey,
    searchText,
  ]);

  const sentinelRef = useInfiniteScroll({
    hasNextPage: hasMore,
    isFetchingNextPage: isFetchingMore,
    fetchNextPage: loadNextPage,
  });

  // Collect all primitive top-level field names present in the live API response.
  // Object-valued fields (e.g. unitAnalysis) are skipped via the typeof check.
  // Any new primitive field that appears in the API response automatically gets
  // an optional column — no code change needed.
  const presentApiFields = useMemo<Set<string>>(() => {
    const fields = new Set<string>();

    allIdentities.forEach((row) => {
      Object.entries(row as unknown as Record<string, unknown>).forEach(
        ([key, value]) => {
          if (
            !ROW_MAPPED_API_FIELDS.has(key) &&
            value !== null &&
            value !== undefined &&
            typeof value !== 'object'
          ) {
            fields.add(key);
          }
        }
      );
    });

    return fields;
  }, [allIdentities]);

  useEffect(() => {
    setColumns(buildColumns(selectedCategory, presentApiFields));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, presentApiFields]);

  // Identity-specific filter dropdown options built from the current API result set.
  const dynamicFilterOptions = useMemo(() => {
    const isHuman = selectedCategory === 'Human';
    const uniqueIds = [
      ...new Set(
        allIdentities.map((r) =>
          isHuman ? r.displayName ?? '—' : r.accountName ?? '—'
        )
      ),
    ];

    return {
      userId: uniqueIds.map((id) => ({ value: id, label: id })),
      jobId: uniqueIds.map((id) => ({ value: id, label: id })),
    };
  }, [allIdentities, selectedCategory]);

  // Client-side filtering on the API response.
  // Filters the data we received from the server so that selections take effect
  // immediately, regardless of whether the backend supports filter params.
  const filteredIdentities = useMemo<IdentitySearchRow[]>(() => {
    let data = allIdentities;

    const statusFilter = filterSelections.status ?? [];

    if (statusFilter.length > 0) {
      data = data.filter((row) => statusFilter.includes(row.evaluationStatus));
    }

    const tierFilter = filterSelections.tier ?? [];

    if (tierFilter.length > 0) {
      data = data.filter((row) =>
        tierFilter.some((t) => row.currentTier.startsWith(t))
      );
    }

    const recommendedTierFilter = filterSelections.recommendedTier ?? [];

    if (recommendedTierFilter.length > 0) {
      data = data.filter((row) =>
        recommendedTierFilter.some((t) => row.recommendedTier.startsWith(t))
      );
    }

    const userIdFilter = filterSelections.userId ?? [];

    if (userIdFilter.length > 0) {
      data = data.filter((row) =>
        userIdFilter.includes(row.displayName ?? '—')
      );
    }

    const jobIdFilter = filterSelections.jobId ?? [];

    if (jobIdFilter.length > 0) {
      data = data.filter((row) => jobIdFilter.includes(row.accountName ?? '—'));
    }

    return data;
  }, [allIdentities, filterSelections]);

  const hasActiveFilters = Object.values(filterSelections).some(
    (v) => v.length > 0
  );

  const sortConfigs = columns
    .filter((c) => c.isSortable)
    .map((c) => ({ key: c.key, sortOptions: SORT_OPTIONS }));

  const rows = filteredIdentities.map((row) => {
    // Spread every primitive API field as a string value.
    // Object-typed fields (e.g. unitAnalysis) are skipped automatically.
    // Fields used elsewhere (displayName → userId, etc.) are excluded via
    // ROW_MAPPED_API_FIELDS. Mandatory columns are overridden below.
    const base: Record<string, unknown> = {};

    Object.entries(row as unknown as Record<string, unknown>).forEach(
      ([key, value]) => {
        if (!ROW_MAPPED_API_FIELDS.has(key) && typeof value !== 'object') {
          base[key] = value != null ? String(value) : '—';
        }
      }
    );

    return {
      ...base,
      id: row.identityId,
      // Human uses 'userId' column key; all non-Human categories use 'jobId'
      ...(selectedCategory === 'Human'
        ? { userId: row.displayName ?? '—' }
        : { jobId: row.accountName ?? '—' }),
      department: row.departmentName ?? '—',
      // Override with rendered/formatted versions for mandatory columns
      currentTier: row.currentTier ? (
        <Chip
          kind='subtleFilled'
          color='warning'
          label={formatTierLabel(row.currentTier)}
          size='small'
          className={getTierChipClass(row.currentTier)}
        />
      ) : (
        '—'
      ),
      recommendedTier: row.recommendedTier ? (
        <Chip
          kind='subtleFilled'
          color='warning'
          label={formatTierLabel(row.recommendedTier)}
          size='small'
          className={getTierChipClass(row.recommendedTier)}
        />
      ) : (
        '—'
      ),
      evaluationStatus: row.evaluationStatus ? (
        <Chip
          kind='subtleFilled'
          label={row.evaluationStatus}
          color={getStatusChipColor(row.evaluationStatus)}
          size='small'
        />
      ) : (
        '—'
      ),
      evaluatedCost: `$${(row.evaluatedCost ?? 0).toLocaleString()}`,
    };
  });

  const handleRowClick = (rowData: { id: string }, rowIndex: number) => {
    console.log('check8', rowIndex);
    setSelectedRowIndex(rowIndex);
    const apiRow = filteredIdentities.find((r) => r.identityId === rowData.id);

    if (apiRow && onRowClick) {
      const primaryId =
        apiRow.identityType === 'Human'
          ? apiRow.displayName ?? '—'
          : apiRow.accountName ?? '—';

      onRowClick({
        id: apiRow.identityId,
        userId: primaryId,
        department: apiRow.departmentName,
        identityType: apiRow.identityType,
        currentTier: apiRow.currentTier,
        currentFue: apiRow.currentUnit,
        recommendedTier: apiRow.recommendedTier,
        evaluatedCost: apiRow.evaluatedCost,
        evaluationStatus:
          apiRow.evaluationStatus as FUEInventoryRow['evaluationStatus'],
        lastActiveAt: apiRow.lastActiveAt,
        daysInactive: apiRow.daysInactive ?? 0,
        assignedRoles: [],
      });
    }
  };

  const lockedIds = LOCKED_FILTERS_BY_CATEGORY[selectedCategory] ?? [];

  const renderTableContent = (): React.ReactElement | null => {
    if (isIdentitiesLoading) {
      return (
        <div className='LicenseManagement-FUEInventoryTable-loadingState'>
          <Loader kind='dots' size='medium' format='inline' color='brand' />
        </div>
      );
    }

    if ((isError || isSearchError) && filteredIdentities.length === 0) {
      return (
        <div className='LicenseManagement-FUEInventoryTable-errorState'>
          <Typography kind='body2' color='--color-foreground-neutral-medium'>
            {localize('Failed to load {category} data', {
              category: selectedCategory,
            })}
          </Typography>
          <Button
            kind='outlined'
            size='small'
            onClick={() => {
              if (onRetry) {
                onRetry();
              }

              refetchSearch().catch(() => {});
            }}>
            {localize('Retry')}
          </Button>
        </div>
      );
    }

    if (identitySearchData !== undefined && filteredIdentities.length === 0) {
      return (
        <div className='LicenseManagement-FUEInventoryTable-noData'>
          <Illustration kind={'Empty' as IllustrationKind} />
          <Typography kind='h2'>
            {localize('No Matching Results Found')}
          </Typography>
          <Typography kind='body2' color='--color-foreground-neutral-medium'>
            {localize('Try updating the filters or check back later.')}
          </Typography>
        </div>
      );
    }

    if (columns.length > 0) {
      return (
        <TableSortable
          columns={columns}
          setColumns={setColumns}
          data={rows}
          sortConfigs={sortConfigs}
          isVirtualizationEnabled={false}
          selectedRowIndex={selectedRowIndex}
          columnConfigs={{
            isDndDisabled: false,
            isHeaderSticky: true,
            isHorizontalScroll: true,
          }}
          onRowClick={handleRowClick}
        />
      );
    }

    return null;
  };

  const rowCountLabel =
    !isIdentitiesLoading &&
    !isIdentitiesFetching &&
    filteredIdentities.length > 0 &&
    identitySearchData !== undefined
      ? `1-${filteredIdentities.length} of ${identitySearchData.totalRecords}`
      : null;

  return (
    <div className='LicenseManagement-FUEInventoryTable'>
      <div className='LicenseManagement-FUEInventoryTable-categoryBar'>
        {identityTypeList.map((category) => (
          <button
            key={category}
            type='button'
            className={`LicenseManagement-FUEInventoryTable-categoryChip${
              selectedCategory === category
                ? ' LicenseManagement-FUEInventoryTable-categoryChip--selected'
                : ''
            }`}
            onClick={() => setSelectedCategory(category)}>
            {category}
          </button>
        ))}
      </div>

      {isFilterBarOpen && (
        <div className='LicenseManagement-FUEInventoryTable-filterRow'>
          {resolvedFilters
            .filter((f: FueFilterDef) => enabledFilterIds.includes(f.id))
            .map((filter: FueFilterDef) => {
              const selectedValues = filterSelections[filter.id] ?? [];
              // API-driven filters already have options; identity filters fall
              // back to dynamicFilterOptions built from mock data
              const resolvedOptions: { value: string; label: string }[] =
                filter.options.length > 0
                  ? filter.options
                  : dynamicFilterOptions[
                      filter.id as keyof typeof dynamicFilterOptions
                    ] ?? [];
              const menuOptions: MenuMultiOption[] = resolvedOptions.map(
                (opt) => ({
                  ...opt,
                  isChecked: selectedValues.includes(opt.value),
                })
              );

              return (
                <MenuMulti
                  key={filter.id}
                  label={filter.label}
                  options={menuOptions}
                  appendLabelsToButton
                  isFilteringDisabled
                  minOptionsForFooter={0}
                  trigger={<ButtonSelect kind='pill' label='' />}
                  onChange={(selected) => {
                    onFilterChange?.(
                      filter.id,
                      (selected ?? [])
                        .filter((o: MenuMultiOption) => o.isChecked)
                        .map((o: MenuMultiOption) => o.value)
                    );
                  }}
                  footerLink2Text='Clear Selection'
                  onFooterLink2Click={() => onFilterChange?.(filter.id, [])}
                />
              );
            })}

          <Menu
            kind='addAndRemove'
            label='Add or Remove Filters'
            className='LicenseManagement-FUEInventoryTable-addRemoveBtn'
            options={resolvedFilters.map(
              (f: FueFilterDef): MenuOption => ({
                value: f.id,
                label: f.label,
                category: f.category,
                isChecked: enabledFilterIds.includes(f.id),
                isDisabled: lockedIds.includes(f.id),
              })
            )}
            onChange={(opt: MenuOption) => {
              if (lockedIds.includes(opt.value)) {
                return;
              }

              if (opt.isChecked) {
                onFilterChange?.(opt.value, []);
                setEnabledFilterIds((prev) =>
                  prev.filter((id) => id !== opt.value)
                );
              } else {
                setEnabledFilterIds((prev) => [...prev, opt.value]);
                setIsAddRemoveMenuOpen(false);
              }
            }}
            isMenuExpanded={isAddRemoveMenuOpen}
            setIsExpanded={setIsAddRemoveMenuOpen}
            inlineMessageText=''
            isInlineMessageVisible={false}
            trigger={
              <ButtonIcon
                kind='outlinedNeutral'
                size='medium'
                ariaLabel='Add filter'
                icon={<Icon kind='Add' size='xSmall' />}
              />
            }
            shouldCloseMenuOnItemClick={false}
            placement='bottom-start'
            isPlacementLocked
          />

          {hasActiveFilters && (
            <button
              type='button'
              className='LicenseManagement-FUEInventoryTable-clearBtn'
              onClick={onClearFilters}>
              {localize('Clear')}
            </button>
          )}
        </div>
      )}

      {rowCountLabel && (
        <Typography
          kind='body2'
          color='--color-foreground-neutral-subtle'
          className='LicenseManagement-FUEInventoryTable-rowCount'>
          {rowCountLabel}
        </Typography>
      )}

      {renderTableContent()}

      {isFetchingMore && !isIdentitiesFetching && (
        <div className='LicenseManagement-FUEInventoryTable-loadingMore'>
          <Loader kind='dots' size='medium' format='inline' color='brand' />
        </div>
      )}

      <div ref={sentinelRef} style={{ height: '1px' }} aria-hidden='true' />
    </div>
  );
}

FUEInventoryTable.defaultProps = {
  onRowClick: undefined,
  isFilterBarOpen: false,
  filterSelections: EMPTY_SELECTIONS,
  onFilterChange: undefined,
  onClearFilters: undefined,
  enabledFilterIds: undefined,
  onEnabledFilterIdsChange: undefined,
  isError: false,
  onRetry: undefined,
  searchText: undefined,
  isPanelOpen: false,
};

export default FUEInventoryTable;
